import i18n from '@opsbase/lib-i18n';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import Self from '@opsbase/lib-self';
import { getStoredValue, setStoredValue } from '@opsbase/lib-storage';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions';
import useSelfSwitchOrg from '@opsbase/react-gql-hooks/self/useSelfSwitchOrg';
import useAuth from '@opsbase/react-useauth';
import AppPageSmall from '@opsbase/web-app/AppPage/AppPageSmall';
import useCss from '@opsbase/web-css';
import OrganisationChooser from '@opsbase/web-data-choosers/OrganisationChooser';
import StaffChooser from '@opsbase/web-data-choosers/StaffChooser';
import TeamboardChooser from '@opsbase/web-data-choosers/TeamboardChooser';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import Button from '@opsbase/web-ui/Button';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo, useState } from 'react';
const makeMyClasses = () => ({
    chooser: {
        paddingLeft: 0,
        minHeight: 28,
        backgroundColor: 'transparent',
        '&>div>div': {
            width: '100%',
        },
    },
    chooserInner: {
        width: '100%',
        minHeight: 0,
        '& button': { width: '100%', padding: '7px 16px', margin: 0 },
    },
});
export function AuthSwitchPage() {
    const [org, setOrg] = useState(getInitialOrgValue());
    const [staff] = useState([]);
    const [busy, setBusy] = useState(false);
    const { api } = useAuth();
    const { goTo } = useNav();
    const { raiseError } = useNotificationActions();
    const { setSelf, setShadow } = useSelfActions();
    const myCss = useCss(makeMyClasses);
    const switchOrg = useSelfSwitchOrg();
    const standardResponseHandler = useMemo(() => r => {
        setShadow(Self.fromData(r.self));
        setStoredValue('shadowAccessToken', r.accessToken);
        setStoredValue('shadowRefreshToken', r.refreshToken);
        setBusy(false);
        setTimeout(() => goTo(Pages.TODO_OPS), 200);
    }, [goTo, setShadow]);
    const standardResponseCatcher = useMemo(() => e => {
        raiseError(e.message);
        setBusy(false);
    }, [raiseError]);
    const handleSelectNoShadow = useCallback(() => {
        var _a;
        setBusy(true);
        switchOrg({ id: (_a = org === null || org === void 0 ? void 0 : org[0]) === null || _a === void 0 ? void 0 : _a.id })
            .then(async (data) => setSelf(Self.fromData(data)))
            .then(() => goTo(Pages.OP_DEFINITIONS))
            .catch(standardResponseCatcher);
    }, [goTo, org, setSelf, standardResponseCatcher, switchOrg]);
    const handleSelectStaff = useCallback(selectedStaff => {
        var _a;
        if (!((_a = selectedStaff === null || selectedStaff === void 0 ? void 0 : selectedStaff[0]) === null || _a === void 0 ? void 0 : _a.id))
            return;
        setBusy(true);
        api.shadow(selectedStaff[0].id, undefined)
            .then(standardResponseHandler)
            .catch(standardResponseCatcher);
    }, [api, standardResponseCatcher, standardResponseHandler]);
    const handleSelectPrimary = useCallback(() => {
        api.shadow(undefined, org[0].id)
            .then(standardResponseHandler)
            .catch(standardResponseCatcher);
    }, [api, org, standardResponseCatcher, standardResponseHandler]);
    const handleSignout = useCallback(() => {
        api.signOut()
            .then(() => goTo(Pages.AUTH_SIGNOUT))
            .catch(standardResponseCatcher);
    }, [api, goTo, standardResponseCatcher]);
    const handleSelectTeamboard = useCallback(teamboard => {
        var _a;
        if (!((_a = teamboard === null || teamboard === void 0 ? void 0 : teamboard[0]) === null || _a === void 0 ? void 0 : _a.id))
            return;
        setBusy(true);
        api.shadowTeambaord(teamboard[0].id, org[0].id)
            .then(standardResponseHandler)
            .catch(standardResponseCatcher);
    }, [api, org, standardResponseHandler, standardResponseCatcher]);
    const handleChangeOrg = useCallback(v => setOrg(v), []);
    return (React.createElement(AppPageSmall, null, !busy && (React.createElement(Vertical, { gutter: true },
        React.createElement(OrganisationChooser, { disabled: busy, value: org, onChange: handleChangeOrg }),
        React.createElement(Vertical, null,
            React.createElement(Button, { disabled: !(org === null || org === void 0 ? void 0 : org.length), color: Palette.positive, label: 'Dont Shadow Anyone', onClick: handleSelectNoShadow }),
            React.createElement(Text, { centered: true }, "or"),
            React.createElement(Button, { disabled: !(org === null || org === void 0 ? void 0 : org.length), label: 'Shadow any Primary Admin', onClick: handleSelectPrimary }),
            React.createElement(Text, { centered: true }, "or"),
            React.createElement(StaffChooser, { disabled: !(org === null || org === void 0 ? void 0 : org.length), noBorder: true, classes: {
                    root: myCss.chooser,
                    inner: myCss.chooserInner,
                }, filters: { organisation: org === null || org === void 0 ? void 0 : org.map(o => o.id) }, value: staff, onChange: handleSelectStaff, selectLabel: 'Shadow a specific User/Staff' }),
            React.createElement(Text, { centered: true }, "or"),
            React.createElement(TeamboardChooser, { disabled: !(org === null || org === void 0 ? void 0 : org.length), noBorder: true, classes: {
                    root: myCss.chooser,
                    inner: myCss.chooserInner,
                }, filters: { organisation: org === null || org === void 0 ? void 0 : org.map(o => o.id) }, value: staff, onChange: handleSelectTeamboard, selectLabel: 'Shadow a specific Teamboard' }),
            React.createElement(Text, { centered: true }, "- - -"),
            React.createElement(Button, { label: i18n.t('sign-out'), onClick: handleSignout }))))));
}
export default AuthSwitchPage;
function getInitialOrgValue() {
    const bits = (getStoredValue('lastOrg') || '').split('###');
    return (bits === null || bits === void 0 ? void 0 : bits.length) ? [{ id: bits[0], title: bits[1] }] : [];
}
